import {createStyles, Fab, FabProps, makeStyles, Theme, Tooltip} from '@material-ui/core';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncError, ServerError} from 'Core/models/Async';
import React, {ReactNode} from 'react';
import {useDispatch} from 'react-redux';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import 'Shared/components/FileDownloadButton.sass';
import {downloadFile} from 'Shared/utilities/fetchHelpers';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';

type FileDownloadFabProps = {
  downloadedFileName: string;
  extension: FileExtension;
  relativeFileLocation: string;
  children: ReactNode;
  startIcon?: ReactNode;
  className?: string;
  displayTooltip: boolean;
  tooltipMessage?: string;
} & FabProps;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fab: {
      position: 'absolute',
      bottom: theme.spacing(2),
      right: theme.spacing(2),
      zIndex: 1,
    },
    extendedIcon: {
      marginRight: theme.spacing(1),
    },
  })
);

const FileDownloadFab = (props: FileDownloadFabProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const {
    downloadedFileName,
    extension = '',
    relativeFileLocation,
    className,
    children,
    startIcon: startIconProp,
    displayTooltip,
    tooltipMessage,
    ...buttonProps
  } = props;

  const defaultTooltipMessage =
  (displayTooltip && !tooltipMessage) || (extension === FileExtension.CSV && !tooltipMessage)
    ? 'Only the first 5,000 records will be extracted'
    : tooltipMessage!;

  let startIcon;
  if (React.isValidElement(startIconProp)) {
    startIcon = React.cloneElement(startIconProp, {
      ...startIconProp.props,
      className: `${startIconProp.props.className} ${classes.extendedIcon}`,
    });
  }

  return (
    <Tooltip title={defaultTooltipMessage} aria-label="info">
      <Fab
        {...buttonProps}
        className={classes.fab}
        id={`${downloadedFileName}-${toKebabCase(defaultTooltipMessage)}`}
        color="primary"
        onClick={() => {
          downloadFile(relativeFileLocation, `${downloadedFileName}${extension}`).then(
            (response: AsyncError | string) => {
              if (typeof response === 'string') {
                dispatch(createSnackbar(response));
              } else {
                if (response instanceof ServerError) {
                  dispatch(createSnackbar('An Error Occurred While Downloading the Enrollment File.'));
                } else {
                  dispatch(createSnackbar(response.message));
                }
              }
            }
          );
        }}
      >
        {startIcon}
        {children}
      </Fab>
    </Tooltip>
  );
};

export default FileDownloadFab;
